import { useEffect } from 'react';

const SITE_NAME = 'FashionSpyder';
const SITE_URL = 'https://fashionspyder.com';
const DEFAULT_OG_IMAGE = 'https://static.prod-images.emergentagent.com/jobs/cb02bfe1-b7d3-4da0-8720-1db898d515c3/images/fdc7a81a0082018f88c964f659fdfe54976c7c16e5759dcb6a657067935e9860.png';
const DEFAULT_TITLE = `${SITE_NAME} | AI Talent Intelligence for Fashion Brands`;
const DEFAULT_DESCRIPTION = 'FashionSpyder is the first AI-powered HR intelligence platform built exclusively for the fashion industry. Discover, match and hire verified fashion professionals from Milan, Paris, London and beyond.';

/* ── tiny helpers ──────────────────────────────────────────────────────── */
const setMeta = (attr, name, content) => {
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.rel = 'canonical';
    document.head.appendChild(el);
  }
  el.href = href;
};

const setLD = (id, schema) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(schema);
};

/* ── SEO component ─────────────────────────────────────────────────────── */
export const SEO = ({ title, description, path = '/', ogImage, noIndex = false, schema }) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const metaDesc = description || DEFAULT_DESCRIPTION;
  const canonical = `${SITE_URL}${path}`;
  const image = ogImage || DEFAULT_OG_IMAGE;

  useEffect(() => {
    document.title = fullTitle;

    // Standard
    setMeta('name', 'description', metaDesc);
    if (noIndex) setMeta('name', 'robots', 'noindex, nofollow');
    setCanonical(canonical);

    // Open Graph
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', metaDesc);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:image', image);

    // Twitter
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', metaDesc);
    setMeta('name', 'twitter:image', image);

    // JSON-LD
    if (schema) {
      const schemas = Array.isArray(schema) ? schema : [schema];
      schemas.forEach((s, i) => setLD(`__seo_ld_${i}__`, s));
    }

    return () => {
      // Restore defaults on unmount
      document.title = DEFAULT_TITLE;
      setMeta('property', 'og:title', DEFAULT_TITLE);
      setMeta('property', 'og:description', DEFAULT_DESCRIPTION);
    };
  }, [fullTitle, metaDesc, canonical, image, noIndex, schema]);

  return null;
};

/* ── Reusable schema objects ───────────────────────────────────────────── */
export const schemaOrganization = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'FashionSpyder',
  url: 'https://fashionspyder.com',
  description: 'AI-powered HR intelligence platform built exclusively for the fashion industry.',
  foundingDate: '2025',
  address: { '@type': 'PostalAddress', addressCountry: 'IT', addressLocality: 'Milan' },
  contactPoint: {
    '@type': 'ContactPoint',
    email: 'info@fashionspyder.com',
    contactType: 'customer service',
    availableLanguage: ['English', 'Italian'],
  },
  sameAs: ['https://linkedin.com/company/fashionspyder', 'https://instagram.com/fashionspyder'],
};

export const schemaWebSite = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'FashionSpyder',
  url: 'https://fashionspyder.com',
  potentialAction: {
    '@type': 'SearchAction',
    target: { '@type': 'EntryPoint', urlTemplate: 'https://fashionspyder.com/gallery?search={search_term_string}' },
    'query-input': 'required name=search_term_string',
  },
};

export const schemaSoftwareApp = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'FashionSpyder',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  url: 'https://fashionspyder.com',
  description: 'AI-powered talent intelligence platform for fashion brand HR teams.',
  offers: [
    { '@type': 'Offer', name: 'RECON', price: '149', priceCurrency: 'EUR' },
    { '@type': 'Offer', name: 'INTELLIGENCE', price: '349', priceCurrency: 'EUR' },
  ],
};

